import SettingsIcon from '@mui/icons-material/Settings';
import { IconButton, Link, Tooltip } from '@mui/material';
import { styled as muiStyled, css, useTheme } from '@mui/material/styles';
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useDashboardDataContext } from './dashboard-context';
import DashboardSkeleton from './dashboard-skeleton';
import ProjectDimension from './dimension';
import DashboardDrillDownDrawer, {
  type DrawerContent,
} from './drill-down-drawer';
import type { DrillDownControlItem } from './drill-down-popover';
import { Governance } from './governance-table';
import { navigateToAssessment } from './helper';
import { LifecycleStepper } from './lifecycle-steper';
import ProjectDetails from './project-details';
import RiskHistoryGraph from './risk-history-graph';
// import NotificationBell from '../../base/notification-bell';
import { assessmentPolicyHasLabel } from '../../assessment/utils/assessment-utils';
import Text from '../../base/text';
import { RenameProject as ProjectName } from '../../dashboard/rename-project';
import { isUserProjectLeadOrOwner } from '../right-panel';

import { useUserProfile } from '~/client/hooks/user-profile';
import {
  aiSystemAssessmentRouteUtil,
  candidateAgentAssessmentRouteUtil,
  candidateModelAssessmentRouteUtil,
  functionalModelAssessmentRouteUtil,
} from '~/client/routes';
import * as GQL from '~/graphql/generated/client';
import { Arranger } from '~c/components/base/arranger';
import Button from '~c/components/base/button';
import { Color, getDesignTokens } from '~c/components/base/core';
import ExpandableStatusList, {
  StatusListItem,
} from '~c/components/base/expandable-status-list';
import PieGraph, { GraphDatum } from '~c/components/base/graph-card';
import Spacer, { spc } from '~c/components/base/spacer';
import StatusBadge, {
  type AlignmentLevel,
} from '~c/components/base/status-badge';

/**
 * Strip the "Control A.6.1.2" prefix from control names.
 * e.g. "Control A.6.1.2 Objectives for responsible development of AI"
 *    → "Objectives for responsible development of AI"
 */
function cleanControlName(name: string): string {
  return name.replace(/^Control\s+[A-Z0-9.]+\s+/i, '').trim() || name;
}

/**
 * Derive a human-readable name from a dot-separated control identifier.
 * e.g. 'com.fairly.nyll144.auditresults.sex' → 'Audit Results - Sex'
 */
function humanizeControlIdentifier(identifier: string): string {
  const parts = identifier.split('.');
  // Take the last 2 meaningful segments (skip common prefixes like com, fairly, ...)
  const meaningful = parts.slice(-2);
  return meaningful
    .map(
      (part) =>
        part
          .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase → separate words
          .replace(/[-_]/g, ' ') // hyphens/underscores → spaces
          .replace(/\b\w/g, (c) => c.toUpperCase()) // capitalize each word
    )
    .join(' - ');
}

type ControlComplianceLabel =
  | 'Fully Compliant'
  | 'Partially Compliant'
  | 'Non Compliant'
  | 'Unavailable';

interface ControlCompliancePalette {
  fully: string;
  partial: string;
  notCompliant: string;
  unavailable: string;
}

/**
 * Maps an answer's `(hasValidValue, riskStatus)` pair to the per-control
 * compliance label and palette colour shown in the dashboard drill-down
 * lists.
 *
 * Centralised so the technology-risk and policy-compliance drill-downs
 * stay in lockstep — previously each had its own if/else chain that
 * collapsed both "no answer" and "unrecognised risk status" to
 * Unavailable, which tripped SonarLint S1871 (duplicate branches) and
 * risked the two chains drifting over time.
 *
 * Semantics (matches the manager-approved per-control dispatcher):
 *   - hasValidValue === false → Unavailable (control visible but no
 *     scorable answer yet).
 *   - riskStatus Low / Medium / High → Fully / Partially / Non
 *     Compliant.
 *   - any other riskStatus (Unavailable, Unverified, null, undefined)
 *     → Unavailable. Keeping a single Unavailable branch means the
 *     "no answer" and "unknown risk status" paths agree by
 *     construction.
 */
function resolveControlComplianceStatus(
  hasValidValue: boolean,
  riskStatus: GQL.AssessmentRiskStatusType | null | undefined,
  palette: ControlCompliancePalette
): { status: ControlComplianceLabel; color: string } {
  if (hasValidValue) {
    switch (riskStatus) {
      case GQL.AssessmentRiskStatusType.Low:
        return { status: 'Fully Compliant', color: palette.fully };
      case GQL.AssessmentRiskStatusType.Medium:
        return { status: 'Partially Compliant', color: palette.partial };
      case GQL.AssessmentRiskStatusType.High:
        return { status: 'Non Compliant', color: palette.notCompliant };
      default:
        break;
    }
  }
  return { status: 'Unavailable', color: palette.unavailable };
}

interface Props {
  className?: string;
}

const DashboardImpl: React.FC<Props> = ({ className }) => {
  const {
    loading,
    error,
    projectName,
    technologyRiskStatusData,
    alignmentRiskStatusData,
    totalNumControls,
    totalNumTestScore,
    completionPercent,
    technologyRiskData,
    numTechnologyRiskControls,
    policyAlignmentData,
    policyControlPieData,
    numTotalPolicyControls,
    sliceRows,
    sliceTotalCount,
    sliceHasMore,
    sliceLoading,
    sliceVariables,
    fetchSlice,
    loadMoreSlice,
    numPolicies,
    assessments,
    project,
    projectDimension,
    projectStages,
    refetch,
    refetchProjectFromId,
  } = useDashboardDataContext();

  const theme = useTheme();
  const t = React.useMemo(
    () => getDesignTokens(theme.palette.mode, theme.accentPalette),
    [theme.palette.mode, theme.accentPalette]
  );

  const { projectId, functionalModelId, candidateId, agentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromCreateAiSystem = Boolean(
    (location.state as { fromCreateAiSystem?: boolean } | null)
      ?.fromCreateAiSystem
  );

  React.useLayoutEffect(() => {
    if (!fromCreateAiSystem) {
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: {} }
    );
  }, [
    fromCreateAiSystem,
    location.hash,
    location.pathname,
    location.search,
    navigate,
  ]);

  const aiSystemHelper = aiSystemAssessmentRouteUtil.useHelper();
  const functionalModelHelper = functionalModelAssessmentRouteUtil.useHelper();
  const candidateModelHelper = candidateModelAssessmentRouteUtil.useHelper();
  const candidateAgentHelper = candidateAgentAssessmentRouteUtil.useHelper();

  const routeUtils = React.useMemo(
    () => ({
      aiSystem: aiSystemHelper,
      functionalModel: functionalModelHelper,
      candidateModel: candidateModelHelper,
      candidateAgent: candidateAgentHelper,
    }),
    [
      aiSystemHelper,
      functionalModelHelper,
      candidateModelHelper,
      candidateAgentHelper,
    ]
  );

  const navigateToCurrentAssessment = React.useCallback(
    (assessmentId: string) =>
      navigateToAssessment({
        assessmentId,
        project: { projectId, candidateId, functionalModelId, agentId },
        routeUtils,
      }),
    [projectId, candidateId, functionalModelId, agentId, routeUtils]
  );

  const { userProfile } = useUserProfile();
  const isAllowedToChangeStage: boolean = isUserProjectLeadOrOwner(
    project as GQL.Project,
    userProfile?.email ?? ''
  );

  const canManageSettings: boolean = isUserProjectLeadOrOwner(
    project as GQL.Project,
    userProfile?.email ?? ''
  );

  const projectStatusColor = (type_: string) => {
    switch (type_) {
      case GQL.DimensionScoreType.Warning:
        return t.status.warning;
      case GQL.DimensionScoreType.Critical:
        return t.status.danger;
      case GQL.DimensionScoreType.Info:
        return t.status.info;
      case GQL.DimensionScoreType.Na:
        return t.textMuted;
      default:
        break;
    }
  };

  const riskStatusColor = (
    status: GQL.AssessmentRiskStatusType | null | undefined
  ) => {
    switch (status) {
      case GQL.AssessmentRiskStatusType.High:
        return t.status.danger;
      case GQL.AssessmentRiskStatusType.Medium:
        return t.status.warning;
      case GQL.AssessmentRiskStatusType.Low:
        return t.status.info;
      default:
        return t.textMuted;
    }
  };

  // Maps the residual risk status to the StatusBadge "alignment" variant
  // value used by the header chip. Mirrors the convention used elsewhere on
  // the dashboard: Low ⇒ Fully Compliant, Medium ⇒ Partially, High ⇒ Not
  // Compliant. Unavailable / null / unrecognised states fall back to the
  // gray "Unavailable" chip — never throw, never invent a status.
  const toAlignmentLevel = (
    status: GQL.AssessmentRiskStatusType | null | undefined
  ): AlignmentLevel => {
    switch (status) {
      case GQL.AssessmentRiskStatusType.Low:
        return 'fully-compliant';
      case GQL.AssessmentRiskStatusType.Medium:
        return 'partially-compliant';
      case GQL.AssessmentRiskStatusType.High:
        return 'not-compliant';
      case GQL.AssessmentRiskStatusType.Unavailable:
      default:
        return 'unavailable';
    }
  };

  const inherentRiskValue =
    project?.inherentRiskStatus ?? GQL.AssessmentRiskStatusType.Unavailable;
  const residualRiskValue =
    project?.riskStatus ?? GQL.AssessmentRiskStatusType.Unavailable;

  // WHY subtitles for risk boxes.
  // Layer-3 tech rollup can produce LOW/MEDIUM/HIGH/UNAVAILABLE, plus the
  // pre-existing UNVERIFIED for projects without verifiers assigned. The
  // MEDIUM case must be enumerated explicitly so partially-compliant tech
  // assessments do not silently fall through to "NO DATA".
  const techRiskLabel = (() => {
    switch (technologyRiskStatusData.riskStatus) {
      case GQL.AssessmentRiskStatusType.Unverified:
        return 'UNVERIFIED';
      case GQL.AssessmentRiskStatusType.Unavailable:
        return 'INCOMPLETE';
      case GQL.AssessmentRiskStatusType.High:
        return 'HIGH';
      case GQL.AssessmentRiskStatusType.Medium:
        return 'MEDIUM';
      case GQL.AssessmentRiskStatusType.Low:
        return 'LOW';
      default:
        return 'NO DATA';
    }
  })();

  // In-scope-for-policy-compliance: not tech-risk AND not a setup-wizard
  // screening policy. Screening policies feed inherent risk via the
  // pre-screening pipeline; counting them here would double-book setup
  // answers as compliance work and skew the rollup verdict.
  const isPolicyComplianceAssessment = React.useCallback(
    (a: GQL.AssessmentFragment) =>
      !a.isTechnologyRiskAssessment &&
      !assessmentPolicyHasLabel(a, 'screening') &&
      !assessmentPolicyHasLabel(a, 'monitoring'),
    []
  );

  const policyAssessmentCount = assessments.filter(
    isPolicyComplianceAssessment
  ).length;
  const alignFullyCompliant =
    policyAlignmentData.find((d) => d.id === 'Fully Compliant')?.value ?? 0;
  const alignDescription =
    policyAssessmentCount === 0
      ? 'No assessments evaluated'
      : `${alignFullyCompliant} of ${policyAssessmentCount} fully compliant`;

  // Assessment-level stats for the Residual Risk description — consistent with the clickable list.
  // Screening (setup-wizard) assessments are excluded from totals so the
  // counts reflect actual compliance work, matching the server-side
  // residual rollup that already filters them out.
  const residualAssessmentStats = React.useMemo(() => {
    const inScope = assessments.filter(
      (a) =>
        !assessmentPolicyHasLabel(a, 'screening') &&
        !assessmentPolicyHasLabel(a, 'monitoring')
    );
    const compliantCount = inScope.filter(
      (a) => a.riskStatus === GQL.AssessmentRiskStatusType.Low
    ).length;
    return { total: inScope.length, compliant: compliantCount };
  }, [assessments]);

  const residualDescription = (() => {
    const { total, compliant } = residualAssessmentStats;
    if (total === 0) {
      return 'No assessments started';
    }
    return `${compliant} of ${total} fully compliant`;
  })();

  const screeningProfile = project?.screeningProfile as
    | {
        classifications?: { key: string; classLabel: string }[];
        flags?: { key: string; label: string }[];
      }
    | null
    | undefined;
  const screeningClassLabels = (screeningProfile?.classifications ?? []).map(
    (c) => c.classLabel
  );
  const screeningFlagLabels = (screeningProfile?.flags ?? []).map(
    (f) => f.label
  );
  const inherentDescription =
    inherentRiskValue === GQL.AssessmentRiskStatusType.Unavailable &&
    screeningClassLabels.length === 0
      ? 'Not screened'
      : inherentRiskValue !== GQL.AssessmentRiskStatusType.Unavailable &&
          !project?.inherentRiskFromClassification
        ? 'No screening risk level: operational-risk fallback'
        : screeningClassLabels.length > 0
          ? [
              `From ${screeningClassLabels.join(' · ')}`,
              ...(screeningFlagLabels.length > 0
                ? [`Flags: ${screeningFlagLabels.join(', ')}`]
                : []),
            ].join(' — ')
          : 'From screening classification';

  // ── Technology Risk drill-down: flat control list keyed by pie slice status ──
  const techDrillDown = React.useMemo<
    Record<string, DrillDownControlItem[]>
  >(() => {
    const byStatus: Record<string, DrillDownControlItem[]> = {};

    // Guard: no assessments or Phase 1 (no answerBundles) means nothing to show
    if (
      assessments.length === 0 ||
      assessments.every((a) => !a.answerBundles || a.answerBundles.length === 0)
    ) {
      return byStatus;
    }

    const isTechRiskControl = (control: {
      answerType: GQL.AnswerType;
      labels?: string[] | null;
    }) =>
      control.answerType === GQL.AnswerType.TestScore ||
      (control.answerType === GQL.AnswerType.Card &&
        (control.labels?.includes('countAsTestScore') ?? false));

    for (const assessment of assessments) {
      if (!assessment.isTechnologyRiskAssessment) {
        continue;
      }

      // Build a lookup from controlIdentifier -> control for this assessment's policy
      const controlMap = new Map<
        string,
        {
          name: string;
          identifier: string;
          answerType: GQL.AnswerType;
          labels?: string[] | null;
        }
      >();
      for (const cb of assessment.policy?.controlBundleList ?? []) {
        for (const ctrl of cb.controls) {
          controlMap.set(ctrl.identifier, ctrl);
        }
      }

      const assessmentName = assessment.policy?.name || assessment.name;
      const assessmentId = assessment._id;
      const onAssessmentClick = () => navigateToCurrentAssessment(assessmentId);

      // Track which tech controls have answers
      const answeredControlIds = new Set<string>();

      for (const bundle of assessment.answerBundles ?? []) {
        for (const answer of bundle.answers ?? []) {
          const control = controlMap.get(answer.controlIdentifier);
          if (!control || !isTechRiskControl(control)) {
            continue;
          }

          answeredControlIds.add(control.identifier);

          // Check if the answer has valid values
          const hasValidValue =
            answer.values &&
            answer.values.length > 0 &&
            answer.values.some(
              (v) => v.value !== null && v.value !== undefined && v.value !== ''
            );

          const { status, color } = resolveControlComplianceStatus(
            Boolean(hasValidValue),
            answer.riskStatus,
            {
              fully: Color.green,
              partial: Color.riskMediumAmber,
              notCompliant: t.status.danger,
              unavailable: t.status.neutral,
            }
          );

          if (!byStatus[status]) {
            byStatus[status] = [];
          }

          byStatus[status].push({
            controlName:
              cleanControlName(control.name) ||
              humanizeControlIdentifier(control.identifier),
            controlIdentifier: control.identifier,
            status,
            color,
            assessmentName,
            assessmentId,
            onAssessmentClick,
          });
        }
      }

      // Find tech risk controls with NO answer at all → Incomplete
      for (const [identifier, control] of controlMap) {
        if (!isTechRiskControl(control)) {
          continue;
        }
        if (answeredControlIds.has(identifier)) {
          continue;
        }

        if (!byStatus['Unavailable']) {
          byStatus['Unavailable'] = [];
        }
        byStatus['Unavailable'].push({
          controlName:
            cleanControlName(control.name) ||
            humanizeControlIdentifier(control.identifier),
          controlIdentifier: identifier,
          status: 'Unavailable',
          color: t.status.neutral,
          assessmentName,
          assessmentId,
          onAssessmentClick,
        });
      }
    }

    return byStatus;
  }, [assessments, navigateToCurrentAssessment, t]);

  // Per-tech-assessment status list. Defers to the server-side Layer-3
  // rollup (assessment.riskStatus) so this list agrees with the Tech Risk
  // pie's top-line label and with the rest of the dashboard. Replacing the
  // legacy worst-wins client rollup is the whole point of the refactor — one
  // bad control should not dominate when org thresholds say otherwise.
  const techAssessmentItems = React.useMemo<StatusListItem[]>(() => {
    return assessments
      .filter((a) => a.isTechnologyRiskAssessment)
      .map((a) => {
        let status: string;
        let color: string;
        switch (a.riskStatus) {
          case GQL.AssessmentRiskStatusType.High:
            status = 'Non Compliant';
            color = Color.red;
            break;
          case GQL.AssessmentRiskStatusType.Medium:
            status = 'Partially Compliant';
            color = Color.riskMediumAmber;
            break;
          case GQL.AssessmentRiskStatusType.Low:
            status = 'Fully Compliant';
            color = Color.green;
            break;
          default:
            status = 'Incomplete';
            color = Color.grey;
        }
        return {
          name: a.policy?.name || a.name,
          status,
          color,
          onClick: () => navigateToCurrentAssessment(a._id),
        };
      });
  }, [assessments, navigateToCurrentAssessment]);

  const techDescription = (() => {
    const status = technologyRiskStatusData.riskStatus;
    const total = techAssessmentItems.length;
    if (status === GQL.AssessmentRiskStatusType.Unverified) {
      return 'No verification assigned';
    }
    if (status === GQL.AssessmentRiskStatusType.Unavailable) {
      return `${total} verification but not submitted`;
    }
    const nonCompliant = techAssessmentItems.filter(
      (i) => i.status === 'Non Compliant' || i.status === 'Partially Compliant'
    ).length;
    if (nonCompliant > 0) {
      return `${nonCompliant} of ${total} non-compliant`;
    }
    const fullyCompliant = techAssessmentItems.filter(
      (i) => i.status === 'Fully Compliant'
    ).length;
    if (total > 0) {
      return `${fullyCompliant} of ${total} fully compliant`;
    }
    return 'No data';
  })();

  const technologyRiskDataWithTooltips = React.useMemo(() => {
    return technologyRiskData.map((d) => {
      const count = d.value;
      return {
        ...d,
        label: `${count} ${d.label}`,
        tooltipLabel: `${count} control${count === 1 ? '' : 's'}`,
      };
    });
  }, [technologyRiskData]);

  // Only HIGH (Non Compliant) policy assessments. Partially-compliant
  // assessments are a distinct Layer-3 bucket and surface in two places
  // already: the verbal `alignDescription` summary above this list, and
  // the full `allPolicyAssessmentItems` list opened from the popover's
  // click-through. Including Medium here would force the popover titled
  // "Non-Compliant" to render rows tagged "Partially Compliant", which
  // contradicts the rollup's own bucket boundaries and confuses auditors.
  const alignNonCompliantItems = React.useMemo<StatusListItem[]>(() => {
    return assessments
      .filter(
        (a) =>
          isPolicyComplianceAssessment(a) &&
          a.riskStatus === GQL.AssessmentRiskStatusType.High
      )
      .map((a) => ({
        name: a.policy?.name || a.name,
        status: 'Non Compliant',
        color: t.status.danger,
        onClick: () => navigateToCurrentAssessment(a._id),
      }));
  }, [
    assessments,
    isPolicyComplianceAssessment,
    navigateToCurrentAssessment,
    t,
  ]);

  const allPolicyAssessmentItems = React.useMemo<StatusListItem[]>(() => {
    const ORDER: Record<string, number> = {
      'Non Compliant': 0,
      'Partially Compliant': 1,
      Compliant: 2,
      Unavailable: 3,
    };
    return assessments
      .filter(isPolicyComplianceAssessment)
      .map((a) => {
        let status: string;
        let color: string;
        switch (a.riskStatus) {
          case GQL.AssessmentRiskStatusType.Low:
            status = 'Compliant';
            color = t.status.success;
            break;
          case GQL.AssessmentRiskStatusType.Medium:
            status = 'Partially Compliant';
            color = t.status.warning;
            break;
          case GQL.AssessmentRiskStatusType.High:
            status = 'Non Compliant';
            color = t.status.danger;
            break;
          default:
            status = 'Unavailable';
            color = t.status.neutral;
        }
        return {
          name: a.policy?.name || a.name,
          status,
          color,
          onClick: () => navigateToCurrentAssessment(a._id),
        };
      })
      .sort((a, b) => (ORDER[a.status] ?? 99) - (ORDER[b.status] ?? 99));
  }, [
    assessments,
    isPolicyComplianceAssessment,
    navigateToCurrentAssessment,
    t,
  ]);

  // Residual risk non-compliant list — all assessments where policy compliance is bad.
  // Uses the same filter logic as residualAssessmentStats so the count always matches the popup.
  // Screening assessments are excluded so this list never includes
  // setup-wizard rows masquerading as residual risk findings.
  const residualNonCompliantItems = React.useMemo<StatusListItem[]>(() => {
    return assessments
      .filter((a) => {
        if (
          assessmentPolicyHasLabel(a, 'screening') ||
          assessmentPolicyHasLabel(a, 'monitoring')
        ) {
          return false;
        }
        return (
          a.riskStatus === GQL.AssessmentRiskStatusType.High ||
          a.riskStatus === GQL.AssessmentRiskStatusType.Medium
        );
      })
      .map((a) => {
        const isPartial = a.riskStatus === GQL.AssessmentRiskStatusType.Medium;
        const status = isPartial ? 'Partially Compliant' : 'Non Compliant';
        const color = isPartial ? t.status.warning : t.status.danger;
        return {
          name: a.policy?.name || a.name,
          status,
          color,
          onClick: () => navigateToCurrentAssessment(a._id),
        };
      });
  }, [assessments, navigateToCurrentAssessment, t]);

  // Same sort and labels as the governance table.
  const allResidualAssessmentItems = React.useMemo<StatusListItem[]>(() => {
    const ORDER: Record<string, number> = {
      'Non Compliant': 0,
      'Partially Compliant': 1,
      Compliant: 2,
      Incomplete: 3,
      Unavailable: 4,
    };
    return assessments
      .filter(
        (a) =>
          !assessmentPolicyHasLabel(a, 'screening') &&
          !assessmentPolicyHasLabel(a, 'monitoring')
      )
      .map((a) => {
        let status: string;
        let color: string;
        switch (a.riskStatus) {
          case GQL.AssessmentRiskStatusType.Low:
            status = 'Compliant';
            color = t.status.success;
            break;
          case GQL.AssessmentRiskStatusType.Medium:
            status = 'Partially Compliant';
            color = t.status.warning;
            break;
          case GQL.AssessmentRiskStatusType.High:
            status = 'Non Compliant';
            color = t.status.danger;
            break;
          case GQL.AssessmentRiskStatusType.Unavailable:
            status = a.isTechnologyRiskAssessment
              ? 'Incomplete'
              : 'Unavailable';
            color = Color.grey;
            break;
          default:
            status = 'Unavailable';
            color = t.status.neutral;
        }
        return {
          name: a.policy?.name || a.name,
          status,
          color,
          onClick: () => navigateToCurrentAssessment(a._id),
        };
      })
      .sort((a, b) => (ORDER[a.status] ?? 99) - (ORDER[b.status] ?? 99));
  }, [assessments, navigateToCurrentAssessment, t]);

  // ── Policy Compliance drill-down: flat control list keyed by pie slice status ──
  // The pie chart groups ASSESSMENTS by their riskStatus (Low/Medium/High → Fully/Partially/Not Compliant).
  // The drill-down shows ALL controls from those assessments, grouped under the
  // assessment's pie slice key, with each control showing its own individual status.
  const policyDrillDown = React.useMemo<
    Record<string, DrillDownControlItem[]>
  >(() => {
    const byStatus: Record<string, DrillDownControlItem[]> = {};

    for (const assessment of assessments) {
      // Policy compliance only: filter out tech-risk AND screening
      // (setup-wizard) assessments. Screening drives inherent risk via the
      // pre-screening pipeline, not compliance.
      if (!isPolicyComplianceAssessment(assessment)) {
        continue;
      }

      // Determine which pie slice this assessment belongs to based on its riskStatus
      let pieSliceKey: string;
      if (assessment.riskStatus === GQL.AssessmentRiskStatusType.Low) {
        pieSliceKey = 'Fully Compliant';
      } else if (
        assessment.riskStatus === GQL.AssessmentRiskStatusType.Medium
      ) {
        pieSliceKey = 'Partially Compliant';
      } else if (assessment.riskStatus === GQL.AssessmentRiskStatusType.High) {
        pieSliceKey = 'Non Compliant';
      } else {
        pieSliceKey = 'Unavailable';
      }

      // Build a lookup from controlIdentifier -> control metadata
      const controlMap = new Map<
        string,
        { name: string; identifier: string }
      >();
      for (const cb of assessment.policy?.controlBundleList ?? []) {
        for (const ctrl of cb.controls) {
          controlMap.set(ctrl.identifier, ctrl);
        }
      }

      const assessmentName = assessment.policy?.name || assessment.name;
      const assessmentId = assessment._id;
      const onAssessmentClick = () => navigateToCurrentAssessment(assessmentId);

      // Track which controls have answers so we can find unanswered ones
      const answeredControlIds = new Set<string>();

      for (const bundle of assessment.answerBundles ?? []) {
        for (const answer of bundle.answers ?? []) {
          const control = controlMap.get(answer.controlIdentifier);
          if (!control) {
            continue;
          }

          answeredControlIds.add(control.identifier);

          // Determine individual control status from the answer
          const hasValues =
            answer.values &&
            answer.values.length > 0 &&
            answer.values.some(
              (v) => v.value !== null && v.value !== undefined && v.value !== ''
            );

          const { status, color } = resolveControlComplianceStatus(
            Boolean(hasValues),
            answer.riskStatus,
            {
              fully: t.status.success,
              partial: t.status.warning,
              notCompliant: t.status.danger,
              unavailable: t.status.neutral,
            }
          );

          if (!byStatus[pieSliceKey]) {
            byStatus[pieSliceKey] = [];
          }

          byStatus[pieSliceKey].push({
            controlName:
              cleanControlName(control.name) ||
              humanizeControlIdentifier(control.identifier),
            controlIdentifier: control.identifier,
            status,
            color,
            assessmentName,
            assessmentId,
            onAssessmentClick,
          });
        }
      }

      // Find controls in the policy that have NO answer at all → Unavailable
      for (const [identifier, control] of controlMap) {
        if (answeredControlIds.has(identifier)) {
          continue;
        }

        if (!byStatus[pieSliceKey]) {
          byStatus[pieSliceKey] = [];
        }

        byStatus[pieSliceKey].push({
          controlName:
            cleanControlName(control.name) ||
            humanizeControlIdentifier(control.identifier),
          controlIdentifier: identifier,
          status: 'Unavailable',
          color: t.status.neutral,
          assessmentName,
          assessmentId,
          onAssessmentClick,
        });
      }
    }

    return byStatus;
  }, [
    assessments,
    isPolicyComplianceAssessment,
    navigateToCurrentAssessment,
    t,
  ]);

  // ── Drill-down drawer (local state, temporary MUI Drawer) ──
  const [drawerContent, setDrawerContent] =
    React.useState<DrawerContent | null>(null);
  const [screeningDetailsOpen, setScreeningDetailsOpen] = React.useState(false);
  const [selectedSlice, setSelectedSlice] = React.useState<{
    kind: 'tech' | 'policy';
    sliceKey: string;
    riskType: GQL.DrillDownRiskType;
    status: GQL.DrillDownComplianceStatus;
  } | null>(null);

  const statusKeyToEnum = React.useCallback(
    (key: string): GQL.DrillDownComplianceStatus => {
      switch (key) {
        case 'Fully Compliant':
          return GQL.DrillDownComplianceStatus.FullyCompliant;
        case 'Partially Compliant':
          return GQL.DrillDownComplianceStatus.PartiallyCompliant;
        case 'Non Compliant':
          return GQL.DrillDownComplianceStatus.NonCompliant;
        default:
          return GQL.DrillDownComplianceStatus.Unavailable;
      }
    },
    []
  );

  const statusEnumToLabel = React.useCallback(
    (s: GQL.DrillDownComplianceStatus): string => {
      switch (s) {
        case GQL.DrillDownComplianceStatus.FullyCompliant:
          return 'Fully Compliant';
        case GQL.DrillDownComplianceStatus.PartiallyCompliant:
          return 'Partially Compliant';
        case GQL.DrillDownComplianceStatus.NonCompliant:
          return 'Non Compliant';
        default:
          return 'Unavailable';
      }
    },
    []
  );

  const sliceDrawerContent = React.useMemo<DrawerContent | null>(() => {
    if (!selectedSlice) {
      return null;
    }
    const { kind, sliceKey } = selectedSlice;

    // Ignore responses from the previous slice when switching slices.
    // Compare against `effectiveId` (functionalModelId / candidateId /
    // agentId / vendorModelId / projectId — whichever is in the URL) so
    // sub-project dashboards (Use Case, Model, Agent, Dataset) match too;
    // using the URL's `projectId` directly would always be the parent
    // AI System on those routes and the drawer would render empty.
    // We don't check `sliceLoading` so loaded pages stay visible during
    // a Load-more.
    const effectiveId =
      candidateId ?? agentId ?? functionalModelId ?? projectId;
    const responseMatches =
      sliceVariables?.projectId === effectiveId &&
      sliceVariables?.riskType === selectedSlice.riskType &&
      sliceVariables?.status === selectedSlice.status;
    const rawRows = responseMatches ? sliceRows : [];

    const sliceControls: DrillDownControlItem[] = rawRows.map((row) => {
      const statusLabel = statusEnumToLabel(row.status);
      const color =
        statusLabel === 'Non Compliant'
          ? t.status.danger
          : statusLabel === 'Partially Compliant'
            ? kind === 'tech'
              ? Color.riskMediumAmber
              : t.status.warning
            : statusLabel === 'Fully Compliant'
              ? kind === 'tech'
                ? Color.green
                : t.status.success
              : t.status.neutral;
      return {
        controlName: row.controlName,
        controlIdentifier: row.controlIdentifier,
        status: statusLabel,
        color,
        assessmentName: row.assessmentName,
        assessmentId: row.assessmentId,
        onAssessmentClick: () => navigateToCurrentAssessment(row.assessmentId),
      };
    });

    const uniqueAssessments = new Set(sliceControls.map((c) => c.assessmentId))
      .size;
    return {
      type: 'control-list',
      title:
        kind === 'tech'
          ? `${sliceKey} Technology Risk Controls`
          : `${sliceKey} Policy Controls`,
      controls: sliceControls,
      uniqueAssessments,
    };
  }, [
    selectedSlice,
    sliceRows,
    sliceVariables,
    projectId,
    functionalModelId,
    candidateId,
    agentId,
    navigateToCurrentAssessment,
    statusEnumToLabel,
    t,
  ]);

  const activeDrawerContent = drawerContent ?? sliceDrawerContent;
  // Only pie-slice drawers show a skeleton.
  const drawerLoading =
    selectedSlice !== null &&
    sliceLoading &&
    (sliceDrawerContent?.type === 'control-list'
      ? sliceDrawerContent.controls.length === 0
      : false);

  const closeDrawer = React.useCallback(() => {
    setDrawerContent(null);
    setSelectedSlice(null);
  }, []);

  // Clear pie-slice selection so the two modes don't fight.
  const openStatusDrawer = React.useCallback(
    (content: Extract<DrawerContent, { type: 'status-list' }>) => {
      setSelectedSlice(null);
      setDrawerContent(content);
    },
    []
  );

  const handleTechSliceClick = React.useCallback(
    (datum: GraphDatum, _event: React.MouseEvent<SVGPathElement>) => {
      const rawKey = String(datum.id);
      const sliceKey = rawKey.replace(/-/g, ' ');
      const status = statusKeyToEnum(sliceKey);
      const riskType = GQL.DrillDownRiskType.Technology;
      setDrawerContent(null);
      setSelectedSlice({ kind: 'tech', sliceKey, riskType, status });
      fetchSlice(riskType, status);
    },
    [fetchSlice, statusKeyToEnum]
  );

  const handlePolicySliceClick = React.useCallback(
    (datum: GraphDatum, _event: React.MouseEvent<SVGPathElement>) => {
      const sliceKey = String(datum.id);
      const status = statusKeyToEnum(sliceKey);
      const riskType = GQL.DrillDownRiskType.Policy;
      setDrawerContent(null);
      setSelectedSlice({ kind: 'policy', sliceKey, riskType, status });
      fetchSlice(riskType, status);
    },
    [fetchSlice, statusKeyToEnum]
  );

  // Show retry instead of an endless skeleton after 25s.
  const [loadTimedOut, setLoadTimedOut] = React.useState(false);
  React.useEffect(() => {
    if (!loading) {
      setLoadTimedOut(false);
      return undefined;
    }
    setLoadTimedOut(false);
    const timeoutId = window.setTimeout(() => setLoadTimedOut(true), 25000);
    return () => window.clearTimeout(timeoutId);
  }, [loading]);

  if ((!loading && error) || (loading && loadTimedOut)) {
    return (
      <div className={className}>
        <Text sz="body16" color="secondary">
          {loadTimedOut
            ? 'Loading is taking longer than expected.'
            : 'Failed to load project. The request may have timed out or encountered an error.'}
        </Text>
        <Spacer pb={spc.s} />
        {refetchProjectFromId && (
          <Button
            onClick={() => refetchProjectFromId()}
            color="primary"
            outlined
          >
            Retry
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      {loading || !project ? (
        <DashboardSkeleton className={className} />
      ) : (
        <>
          <Arranger dir="row" justify="space-between" alignItems="center">
            {/* Left: project name + compliance chip (chip mirrors the
                Residual Risk card; stays in sync via the shared
                project.riskStatus field). */}
            <Arranger dir="row" alignItems="center" sx={{ gap: 1.5 }}>
              <ProjectName
                projectName={projectName}
                id={project?._id!}
                className={className}
              />
              <StatusBadge
                variant="alignment"
                value={toAlignmentLevel(residualRiskValue)}
              />
            </Arranger>
            <Arranger dir="row" alignItems="center" sx={{ gap: 0.5 }}>
              {/* Bell shows actual notifications - visible to ALL users */}
              {/* <NotificationBell variant="light" /> */}

              {/* Gear opens project settings - only for project Owner */}
              {canManageSettings && (
                <Tooltip title="Project Settings">
                  <IconButton
                    onClick={() => navigate(`/ai-system/${projectId}/settings`)}
                    size="medium"
                    sx={{
                      color: 'text.secondary',
                      '& .MuiSvgIcon-root': { fontSize: '1.5rem' },
                    }}
                  >
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Arranger>
          </Arranger>
          {/* <Spacer pb={spc.xs} /> */}
          <LifecycleStepper
            projectStages={projectStages}
            isAllowed={isAllowedToChangeStage}
          />
          <Spacer pb={spc.xs} />
          <div className="details-grid">
            <ProjectDetails
              value={inherentRiskValue}
              details="Inherent Risk"
              color={riskStatusColor(inherentRiskValue)}
              description={
                screeningClassLabels.length > 0 ? (
                  <>
                    {inherentDescription}{' '}
                    <Link
                      component="button"
                      underline="hover"
                      onClick={() => setScreeningDetailsOpen(true)}
                      sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}
                    >
                      Why?
                    </Link>
                  </>
                ) : (
                  inherentDescription
                )
              }
            />
            <ProjectDetails
              value={residualRiskValue}
              details="Residual Risk"
              color={riskStatusColor(residualRiskValue)}
              description={
                <ExpandableStatusList
                  summary={residualDescription}
                  items={residualNonCompliantItems}
                  popoverTitle="Residual Risk"
                  onTriggerClick={
                    allResidualAssessmentItems.length > 0
                      ? () => {
                          openStatusDrawer({
                            type: 'status-list',
                            title: 'Residual Risk',
                            items: allResidualAssessmentItems,
                          });
                        }
                      : undefined
                  }
                />
              }
            />
            <ProjectDetails
              value={techRiskLabel}
              details="Technology Risk"
              color={technologyRiskStatusData.color}
              description={
                <ExpandableStatusList
                  summary={techDescription}
                  items={techAssessmentItems}
                  popoverTitle="Technology Risk"
                  onTriggerClick={
                    techAssessmentItems.length > 0
                      ? () => {
                          openStatusDrawer({
                            type: 'status-list',
                            title: 'Technology Risk',
                            items: techAssessmentItems,
                          });
                        }
                      : undefined
                  }
                />
              }
            />
            <ProjectDetails
              value={alignmentRiskStatusData.riskStatus}
              details="Policy Compliance Risk"
              color={alignmentRiskStatusData.color}
              description={
                <ExpandableStatusList
                  summary={alignDescription}
                  items={alignNonCompliantItems}
                  popoverTitle="Policy Alignment Risk"
                  onTriggerClick={
                    allPolicyAssessmentItems.length > 0
                      ? () => {
                          openStatusDrawer({
                            type: 'status-list',
                            title: 'Policy Alignment Risk',
                            items: allPolicyAssessmentItems,
                          });
                        }
                      : undefined
                  }
                />
              }
            />
          </div>

          <Spacer pb={spc.s} />
          {projectDimension && projectDimension.length > 0 && (
            <>
              <div className="grid">
                {projectDimension.map((item) => (
                  <ProjectDimension
                    key={item._id}
                    value={item?.ds[0].score}
                    name={item.name + ' : ' + (item?.ds[0].name ?? '')}
                    description={item?.ds[0].description ?? ''}
                    color={projectStatusColor(item?.ds[0].type) ?? ''}
                  />
                ))}
              </div>
              <Spacer pb={spc.s} />
            </>
          )}
          <div className="grid">
            <PieGraph
              className="pie-graph"
              data={technologyRiskDataWithTooltips}
              subHeader={
                <ExpandableStatusList
                  summary={`${numTechnologyRiskControls} technology risk controls across ${techAssessmentItems.length} assessments`}
                  items={techAssessmentItems}
                  popoverTitle="Technology Risk Assessments"
                  onTriggerClick={
                    techAssessmentItems.length > 0
                      ? () => {
                          openStatusDrawer({
                            type: 'status-list',
                            title: 'Technology Risk Assessments',
                            subtitle: `${techAssessmentItems.length} ${techAssessmentItems.length === 1 ? 'item' : 'items'}`,
                            items: techAssessmentItems,
                          });
                        }
                      : undefined
                  }
                />
              }
              title="Technology Risk"
              donutOpt={{
                title: numTechnologyRiskControls,
                description: 'Technology Risk Controls',
              }}
              onSliceClick={handleTechSliceClick}
            />

            <PieGraph
              subHeader={
                <ExpandableStatusList
                  summary={`${numTotalPolicyControls} policy controls across ${numPolicies} assessments`}
                  items={alignNonCompliantItems}
                  popoverTitle="Policy Compliance Assessments"
                  onTriggerClick={
                    allPolicyAssessmentItems.length > 0
                      ? () => {
                          openStatusDrawer({
                            type: 'status-list',
                            title: 'Policy Compliance Assessments',
                            subtitle: `${allPolicyAssessmentItems.length} ${allPolicyAssessmentItems.length === 1 ? 'assessment' : 'assessments'}`,
                            items: allPolicyAssessmentItems,
                          });
                        }
                      : undefined
                  }
                />
              }
              title="Policy Compliance Risk"
              data={policyControlPieData}
              donutOpt={{
                title: numTotalPolicyControls,
                description: 'Policy Controls',
              }}
              onSliceClick={handlePolicySliceClick}
            />
          </div>

          {project?.riskHistory && project.riskHistory.length > 0 && (
            <>
              <Spacer pb={spc.s} />
              <RiskHistoryGraph riskHistory={project.riskHistory} />
            </>
          )}

          <Spacer pb={spc.s} />
          <Governance
            assessments={assessments}
            project_={project as GQL.Project}
            refetch={refetch}
            screeningDetailsOpen={screeningDetailsOpen}
            onScreeningDetailsOpenChange={setScreeningDetailsOpen}
          />
        </>
      )}
      <DashboardDrillDownDrawer
        content={activeDrawerContent}
        // Pass raw sliceLoading so Load-more disables during fetch.
        // The drawer hides the skeleton once at least one row exists.
        loading={selectedSlice !== null ? sliceLoading : drawerLoading}
        hasMore={selectedSlice !== null && sliceHasMore}
        totalCount={selectedSlice !== null ? sliceTotalCount : 0}
        onLoadMore={selectedSlice !== null ? loadMoreSlice : undefined}
        onClose={closeDrawer}
      />
    </div>
  );
};
export const Dashboard = muiStyled(DashboardImpl)(({ theme }) => {
  const tokens = getDesignTokens(
    theme.palette.mode === 'dark' ? 'dark' : 'light',
    theme.accentPalette
  );
  return css`
    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 24px;
    }

    .details-grid {
      display: grid;
      gap: 24px;
      ${theme.breakpoints.up('sm')} {
        grid-template-columns: repeat(4, minmax(150px, 1fr));
      }
      ${theme.breakpoints.down('sm')} {
        grid-template-columns: minmax(150px, 1fr);
      }
    }

    .details-grid-half {
      display: grid;
      gap: 24px;
      ${theme.breakpoints.up('sm')} {
        grid-template-columns: repeat(2, minmax(150px, 1fr));
      }
      ${theme.breakpoints.down('sm')} {
        grid-template-columns: minmax(150px, 1fr);
      }
    }

    .MuiCard-root {
      border: 1px solid ${tokens.borderDefault};
    }
  `;
});
